import * as THREE from 'three';
import { createSceneRig } from './scene/sceneSetup';
import { AnimationLoop } from './scene/animationLoop';
import { ViewModeController } from './scene/viewModes';
import { createBrainGeometry } from './brain/createBrainGeometry';
import { loadCortexMesh, mniToMeshSpace } from './brain/loadCortexMesh';
import { createCerebellumGeometry } from './brain/createCerebellum';
import { createBrainStemGeometry } from './brain/createBrainStem';
import { createWireframeMaterial, createGlowSpriteTexture, lerpColor } from './brain/materials';
import { RegionNodes } from './brain/regionNodes';
import { LabelSprites } from './brain/labelSprites';
import { NeuralActivityParticles } from './particles/neuralActivityParticles';
import { SynapseSparkles } from './particles/synapseSparkles';
import { BurstEffects } from './particles/burstEffects';
import { NervousSystemView } from './nervous/nervousSystem';
import { SignalPulses } from './nervous/signalPulses';
import { SUBSTANCE_LIST } from './pharmacology/substances/index';
import type { NervousSystem, RegionId, Substance } from './pharmacology/types';
import { IDLE_SYSTEM_ACTIVITY } from './simulation/doseResponse';
import { SimulationState } from './simulation/simulationState';
import { SubstanceBrowser } from './ui/SubstanceBrowser';
import { InfoPanel } from './ui/InfoPanel';
import { SystemActivityPanel } from './ui/SystemActivityPanel';
import { DoseTimeControls } from './ui/DoseTimeControls';
import { ConcentrationGraph } from './ui/ConcentrationGraph';
import { requireElement } from './ui/domHelpers';

const DEFAULT_WIREFRAME_COLOR = 0x00c8ee;
const DEFAULT_PARTICLE_COLOR = 0x44ccff;
const DEFAULT_AURA_COLOR = 0x3388cc;
const EASE_FACTOR = 0.08;

/**
 * Loads the real cortical surface, falling back to the procedural lobe geometry
 * if the asset is missing or malformed. A blank screen is a worse failure than a
 * stylized brain, and the fallback is tested in its own right.
 */
async function loadCortexGeometry(): Promise<THREE.BufferGeometry> {
  try {
    return await loadCortexMesh(`${import.meta.env.BASE_URL}brain-cortex.bin`);
  } catch (error) {
    console.warn('THE HUB: falling back to procedural cortex geometry —', error);
    return createBrainGeometry();
  }
}

// ── Scene ────────────────────────────────────────────────────────────────
const container = requireElement<HTMLDivElement>('canvas-container');
const { scene, camera, renderer, controls } = createSceneRig(container);

const brainGroup = new THREE.Group();
scene.add(brainGroup);

const glowTexture = createGlowSpriteTexture();

// ── Cerebrum ─────────────────────────────────────────────────────────────
const brainGeometry = await loadCortexGeometry();

// An opaque shell just inside the wireframe. Without it every triangle of a real
// cortical surface draws at once — front and back faces together — and the
// wireframe collapses into illegible fuzz.
const brainShellMaterial = new THREE.MeshBasicMaterial({
  color: 0x040b16,
  side: THREE.FrontSide,
});
const brainShellMesh = new THREE.Mesh(brainGeometry, brainShellMaterial);
brainShellMesh.scale.setScalar(0.994);
brainGroup.add(brainShellMesh);

const brainMaterial = createWireframeMaterial(DEFAULT_WIREFRAME_COLOR, 0.55);
const brainMesh = new THREE.Mesh(brainGeometry, brainMaterial);
brainGroup.add(brainMesh);

// ── Cerebellum ───────────────────────────────────────────────────────────
const cerebellumMaterial = createWireframeMaterial(0x00b8d8, 0.7);
const cerebellumMesh = new THREE.Mesh(createCerebellumGeometry(), cerebellumMaterial);
cerebellumMesh.position.set(...mniToMeshSpace(0, -60, -35));
cerebellumMesh.rotation.x = -0.16;
brainGroup.add(cerebellumMesh);

// ── Brain stem ───────────────────────────────────────────────────────────
const stemMaterial = createWireframeMaterial(0x0099bb, 0.65);
const stemMesh = new THREE.Mesh(createBrainStemGeometry(), stemMaterial);
stemMesh.position.set(...mniToMeshSpace(0, -26, -34));
stemMesh.rotation.x = -0.13;
brainGroup.add(stemMesh);

// ── Nervous system beyond the brain ──────────────────────────────────────
const nervousSystem = new NervousSystemView();
brainGroup.add(nervousSystem.group);

const signalPulses = new SignalPulses(nervousSystem.pathways, glowTexture);
brainGroup.add(signalPulses.points);

// ── Region nodes + labels ────────────────────────────────────────────────
const regionNodes = new RegionNodes();
brainGroup.add(regionNodes.group);

const labelSprites = new LabelSprites();
brainGroup.add(labelSprites.group);

// ── Particle systems ─────────────────────────────────────────────────────
const neuralParticles = new NeuralActivityParticles(glowTexture);
brainGroup.add(neuralParticles.points);

const synapseSparkles = new SynapseSparkles(brainGeometry, glowTexture);
brainGroup.add(synapseSparkles.points);

const burstEffects = new BurstEffects(glowTexture);
brainGroup.add(burstEffects.points);

// ── UI ───────────────────────────────────────────────────────────────────
const simulationState = new SimulationState();
const infoPanel = new InfoPanel();
const systemActivityPanel = new SystemActivityPanel();
const concentrationGraph = new ConcentrationGraph();
const aura1 = requireElement<HTMLElement>('bg-aura');
const aura2 = requireElement<HTMLElement>('bg-aura-2');

const doseTimeControls = new DoseTimeControls((dose, tHours) => {
  simulationState.setDoseAndTime(dose, tHours);
  refreshReadouts();
});

new SubstanceBrowser(SUBSTANCE_LIST, (substance) => {
  simulationState.setSubstance(substance);
  doseTimeControls.setSubstance(substance);
  infoPanel.show(substance);
  refreshReadouts();
});

const viewModes = new ViewModeController(camera, controls);
const viewToggle = requireElement<HTMLButtonElement>('view-toggle');
viewToggle.addEventListener('click', () => {
  const mode = viewModes.toggle();
  viewToggle.textContent = mode === 'brain' ? '🧍 Whole nervous system' : '🧠 Brain only';
});

const learnToggle = requireElement<HTMLButtonElement>('learn-toggle');
let learnMode = false;
learnToggle.addEventListener('click', () => {
  learnMode = !learnMode;
  learnToggle.setAttribute('aria-pressed', String(learnMode));
  learnToggle.textContent = learnMode ? '📘 Learning…' : '📘 Learn Mode';
  labelSprites.setVisible(learnMode);
});

// On narrow screens the info panel is a toggled overlay rather than a fixture.
const panelToggle = requireElement<HTMLButtonElement>('panel-toggle');
const rightColumn = requireElement<HTMLElement>('right-column');
panelToggle.addEventListener('click', () => {
  const open = rightColumn.classList.toggle('open');
  panelToggle.setAttribute('aria-expanded', String(open));
});

function refreshReadouts(): void {
  const substance = simulationState.substance;
  const result = simulationState.compute();
  const color = substance ? substance.colorPalette.primary : '#00c8ee';

  concentrationGraph.render(substance, simulationState.doseOrReleaseLevel, simulationState.timeHours, color);
  systemActivityPanel.update(result?.systemActivity ?? null);
}

doseTimeControls.setSubstance(null);
infoPanel.show(null);
refreshReadouts();

// ── Eased visual state ───────────────────────────────────────────────────
// Targets are recomputed from the live PK/PD result each frame; the displayed
// values ease toward them so substance switches and slider drags read as
// transitions rather than jumps.
const displayed = {
  wireframeColor: new THREE.Color(DEFAULT_WIREFRAME_COLOR),
  particleColor: new THREE.Color(DEFAULT_PARTICLE_COLOR),
  auraColor: new THREE.Color(DEFAULT_AURA_COLOR),
  particleSpeed: 1,
  synapseActivity: 1,
  burstFrequency: 0.03,
  rotationSpeed: 0.4,
  systemActivity: { ...IDLE_SYSTEM_ACTIVITY },
};

interface VisualTargets {
  wireframeColor: THREE.Color;
  particleColor: THREE.Color;
  auraColor: THREE.Color;
  particleSpeed: number;
  synapseActivity: number;
  burstFrequency: number;
  rotationSpeed: number;
  regionActivity: Partial<Record<RegionId, number>> | undefined;
  systemActivity: Record<NervousSystem, number>;
  intensity: number;
}

function computeVisualTargets(substance: Substance | null, elapsedSeconds: number): VisualTargets {
  const result = simulationState.compute();

  if (!substance || !result) {
    return {
      wireframeColor: new THREE.Color(DEFAULT_WIREFRAME_COLOR),
      particleColor: new THREE.Color(DEFAULT_PARTICLE_COLOR),
      auraColor: new THREE.Color(DEFAULT_AURA_COLOR),
      particleSpeed: 1,
      synapseActivity: 1,
      burstFrequency: 0.03,
      rotationSpeed: 0.4,
      regionActivity: undefined,
      systemActivity: { ...IDLE_SYSTEM_ACTIVITY },
      intensity: 0,
    };
  }

  const intensity = result.peakOccupancy;
  const { primary, secondary, particleSpeedMultiplier, rainbowMode } = substance.colorPalette;

  let wireframeColor = lerpColor(DEFAULT_WIREFRAME_COLOR, primary, intensity);
  let particleColor = lerpColor(DEFAULT_PARTICLE_COLOR, secondary, intensity);
  let auraColor = lerpColor(DEFAULT_AURA_COLOR, primary, intensity);

  if (rainbowMode && intensity > 0.15) {
    const hue = (elapsedSeconds * 0.15) % 1;
    const blend = Math.min(1, (intensity - 0.15) / 0.5);
    wireframeColor = wireframeColor.lerp(new THREE.Color().setHSL(hue, 0.8, 0.6), blend);
    particleColor = particleColor.lerp(new THREE.Color().setHSL((hue + 0.3) % 1, 0.85, 0.6), blend);
    auraColor = auraColor.lerp(new THREE.Color().setHSL((hue + 0.6) % 1, 0.75, 0.55), blend);
  }

  return {
    wireframeColor,
    particleColor,
    auraColor,
    particleSpeed: 1 + (particleSpeedMultiplier - 1) * intensity,
    synapseActivity: 1 + 3 * intensity,
    burstFrequency: 0.03 + 0.3 * intensity,
    rotationSpeed: 0.4 + 0.4 * (particleSpeedMultiplier - 1) * intensity,
    regionActivity: result.regionActivity,
    systemActivity: result.systemActivity,
    intensity,
  };
}

// ── Animation loop ───────────────────────────────────────────────────────
const animationLoop = new AnimationLoop(({ deltaSeconds, elapsedSeconds, frozen }) => {
  const targets = computeVisualTargets(simulationState.substance, elapsedSeconds);

  displayed.wireframeColor.lerp(targets.wireframeColor, EASE_FACTOR);
  displayed.particleColor.lerp(targets.particleColor, EASE_FACTOR);
  displayed.auraColor.lerp(targets.auraColor, EASE_FACTOR);
  displayed.particleSpeed += (targets.particleSpeed - displayed.particleSpeed) * EASE_FACTOR;
  displayed.synapseActivity += (targets.synapseActivity - displayed.synapseActivity) * EASE_FACTOR;
  displayed.burstFrequency += (targets.burstFrequency - displayed.burstFrequency) * EASE_FACTOR;
  displayed.rotationSpeed += (targets.rotationSpeed - displayed.rotationSpeed) * EASE_FACTOR;
  for (const key of Object.keys(displayed.systemActivity) as NervousSystem[]) {
    displayed.systemActivity[key] +=
      (targets.systemActivity[key] - displayed.systemActivity[key]) * EASE_FACTOR;
  }

  brainMaterial.color.copy(displayed.wireframeColor);
  cerebellumMaterial.color.copy(displayed.wireframeColor).multiplyScalar(0.85);
  stemMaterial.color.copy(displayed.wireframeColor).multiplyScalar(0.75);

  neuralParticles.setColor(displayed.particleColor);
  neuralParticles.update(displayed.particleSpeed);
  synapseSparkles.update(displayed.synapseActivity, displayed.particleColor);
  burstEffects.update(
    deltaSeconds,
    displayed.burstFrequency,
    brainGeometry,
    displayed.particleColor,
    targets.intensity,
  );

  nervousSystem.setSystemActivity(displayed.systemActivity, displayed.wireframeColor);
  signalPulses.update(deltaSeconds, displayed.systemActivity, displayed.particleColor, true);

  regionNodes.setActivity(targets.regionActivity, displayed.wireframeColor);

  // A camera transition drives the view itself, so auto-rotation is suspended
  // until it settles — otherwise the two fight and the motion reads as a wobble.
  const transitioning = viewModes.update(deltaSeconds);
  controls.autoRotate = !frozen && !transitioning;
  controls.autoRotateSpeed = displayed.rotationSpeed;
  controls.update();

  const auraHex = `#${displayed.auraColor.getHexString()}`;
  aura1.style.background = `radial-gradient(circle, ${auraHex} 0%, transparent 70%)`;
  aura1.style.opacity = String(0.12 + 0.15 * targets.intensity);
  aura2.style.opacity = String(0.06 + 0.12 * targets.intensity);

  if (!frozen) {
    brainGroup.position.set(
      Math.cos(elapsedSeconds * 0.45) * 0.04,
      Math.sin(elapsedSeconds * 0.6) * 0.08,
      0,
    );
  }

  renderer.render(scene, camera);
});

animationLoop.start();
